import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../../../shared/lib/supabase'
import { useAssessment } from '../../../../../contexts/AssessmentContext'
import { useAIJobs } from '../../../../../contexts/AIJobsContext'
import { exportIntelligencePDF } from '../../../../../shared/utils/intelligencePdfExport'

function fmt(v) { return v || 'N/A' }

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  )
}

function Badge({ children, color = 'gray' }) {
  const colors = {
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  }
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors[color] || colors.gray}`}>{children}</span>
}

function Grade({ grade, size = 'lg' }) {
  const colors = { A: 'text-green-600 bg-green-50 border-green-200', B: 'text-green-500 bg-green-50 border-green-200', C: 'text-amber-500 bg-amber-50 border-amber-200', D: 'text-orange-500 bg-orange-50 border-orange-200', F: 'text-red-500 bg-red-50 border-red-200' }
  const s = size === 'lg' ? 56 : 44
  const fs = size === 'lg' ? 28 : 20
  return <div style={{ width: s, height: s, fontSize: fs, lineHeight: 1, borderRadius: 12, flexShrink: 0 }} className={`border flex items-center justify-center font-bold ${colors[grade] || 'text-gray-400 bg-gray-50 border-gray-200'}`}>{grade}</div>
}

function ClientIntelligenceTab({ client }) {
  const { updateClient } = useAssessment()
  const { startJob, getJob } = useAIJobs()
  const [enrichmentData, setEnrichmentData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('business')
  const [error, setError] = useState(null)
  const [domainInput, setDomainInput] = useState(client.domain || '')
  const [contextInput, setContextInput] = useState(client.enrichmentContext || '')
  const [showRegenerateModal, setShowRegenerateModal] = useState(false)
  const [regenName, setRegenName] = useState(client.name || '')
  const [regenDomain, setRegenDomain] = useState(client.domain || '')
  const [regenContext, setRegenContext] = useState(client.enrichmentContext || '')

  const jobKey = `client-enrich:${client.id}`
  const job = getJob(jobKey)
  const generating = job?.status === 'running'

  // Load persisted enrichment from DB
  const loadEnrichment = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error: fetchErr } = await supabase
        .from('client_enrichment')
        .select('*')
        .eq('client_id', client.id)
        .maybeSingle()
      if (fetchErr) throw fetchErr
      setEnrichmentData(data || null)
    } catch (err) {
      console.error('[ClientIntelligence] Load error:', err)
    } finally {
      setLoading(false)
    }
  }, [client.id])

  useEffect(() => { loadEnrichment() }, [loadEnrichment])

  // When job completes, save to DB and refresh
  useEffect(() => {
    if (job?.status !== 'success') return
    const result = job.result
    if (!result || result._saved) return

    const saveEnrichment = async () => {
      try {
        const row = {
          client_id: client.id,
          enrichment: result.enrichment || {},
          dns: result.dns || {},
          ssl: result.ssl || {},
          whois: result.whois || {},
          ct_subdomains: result.ctSubdomains || [],
          shodan: result.shodan || {},
          generated_at: new Date().toISOString()
        }
        const { error: upsertErr } = await supabase
          .from('client_enrichment')
          .upsert(row, { onConflict: 'client_id' })
        if (upsertErr) throw upsertErr
        result._saved = true
        loadEnrichment()
      } catch (err) {
        console.error('[ClientIntelligence] Save error:', err)
        setError('Enrichment generated but failed to save: ' + err.message)
      }
    }
    saveEnrichment()
  }, [job?.status, job?.result, client.id, loadEnrichment])

  useEffect(() => {
    if (job?.status === 'error') setError(job.error)
  }, [job?.status, job?.error])

  const runEnrichment = async (name, domain, context) => {
    if (!domain) {
      setError('A primary domain is required to generate intelligence')
      return
    }
    setError(null)

    // Save domain/context to client if changed
    const updates = {}
    if (domain !== client.domain) updates.domain = domain
    if ((context || '') !== (client.enrichmentContext || '')) updates.enrichmentContext = context || null
    if (name !== client.name) updates.name = name
    if (Object.keys(updates).length > 0) {
      await updateClient(client.id, updates)
    }

    startJob(jobKey, async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/demo/enrich', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
        },
        body: JSON.stringify({
          companyName: name,
          domain,
          additionalContext: context || undefined
        })
      })
      if (!res.ok) {
        // Try JSON first, fall back to raw text so we always surface a real reason.
        const raw = await res.text().catch(() => '')
        let detail = ''
        try {
          const parsed = JSON.parse(raw)
          detail = parsed.error || parsed.message || raw
        } catch {
          detail = raw
        }
        const trimmed = (detail || '').toString().slice(0, 500).trim()
        throw new Error(`Enrichment failed (${res.status}): ${trimmed || 'no detail returned'}`)
      }
      return await res.json()
    }).catch(err => {
      console.error('[ClientIntelligence] Generation failed:', err)
    })
  }

  const handleGenerate = () => {
    runEnrichment(client.name, domainInput.trim(), contextInput.trim())
  }

  const handleRegenerate = () => {
    setShowRegenerateModal(false)
    setDomainInput(regenDomain.trim())
    setContextInput(regenContext.trim())
    runEnrichment(regenName.trim() || client.name, regenDomain.trim(), regenContext.trim())
  }

  const openRegenerateModal = () => {
    setRegenName(client.name || '')
    setRegenDomain(client.domain || domainInput || '')
    setRegenContext(client.enrichmentContext || contextInput || '')
    setShowRegenerateModal(true)
  }

  const e = enrichmentData?.enrichment
  const dns = enrichmentData?.dns
  const ssl = enrichmentData?.ssl
  const sslLabs = enrichmentData?.sslLabs
  const observatory = enrichmentData?.observatory
  const whois = enrichmentData?.whois

  const tabs = [
    { id: 'business', label: 'Business Context' },
    { id: 'security', label: 'Security Posture' },
    { id: 'engagement', label: 'Engagement Intel' }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    )
  }

  // No enrichment yet — show generate UI
  if (!e) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Client Intelligence</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            AI-powered external intelligence from DNS, SSL, WHOIS, and certificate transparency data.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="max-w-lg mx-auto text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Generate Client Intelligence</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Enter the client's primary domain to run an AI-powered external analysis including DNS reconnaissance, SSL certificate inspection, WHOIS data, and subdomain enumeration.
            </p>

            <div className="space-y-3 text-left">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Domain</label>
                <input
                  type="text"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder="e.g., acmefinancial.com"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Additional Context <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={contextInput}
                  onChange={(e) => setContextInput(e.target.value)}
                  placeholder="e.g., 200 employees, processes credit cards"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={generating || !domainInput.trim()}
              className="btn-primary mt-6 disabled:opacity-50"
            >
              {generating ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Analyzing...</>
              ) : (
                <><svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>Generate Intelligence</>
              )}
            </button>
            {generating && <p className="text-xs text-gray-400 mt-2">This typically takes 15-20 seconds</p>}
          </div>
        </div>
      </div>
    )
  }

  // Enrichment exists — show results
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Client Intelligence</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Generated {enrichmentData.generated_at ? new Date(enrichmentData.generated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'recently'}
            {client.domain ? ` from ${client.domain}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {e.securityPosture?.overallGrade && <Grade grade={e.securityPosture.overallGrade} />}
          {e.confidence && <Badge color={e.confidence.overall === 'high' ? 'green' : e.confidence.overall === 'medium' ? 'amber' : 'red'}>{e.confidence.overall} confidence</Badge>}
          <button
            onClick={() => exportIntelligencePDF({
              companyName: client.name,
              enrichment: e,
              dns, ssl, whois,
              ctSubdomains: enrichmentData?.ct_subdomains,
              shodan: enrichmentData?.shodan
            })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Export PDF
          </button>
          <button
            onClick={openRegenerateModal}
            disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {generating ? (
              <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600" />Regenerating...</>
            ) : (
              <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>Regenerate</>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ backgroundColor: '#012438' }} />}
            </button>
          ))}
        </nav>
      </div>

      {/* Business Context */}
      {activeTab === 'business' && (
        <div className="space-y-5">
          <Section title="Company Profile">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{e.companyProfile?.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Industry', value: e.companyProfile?.industry },
                { label: 'Sub-Industry', value: e.companyProfile?.subIndustry },
                { label: 'Business Model', value: e.companyProfile?.businessModel },
                { label: 'Est. Size', value: e.companyProfile?.estimatedSize },
                { label: 'Est. Revenue', value: e.companyProfile?.estimatedRevenue },
                { label: 'Headquarters', value: e.companyProfile?.headquarters },
                { label: 'Years in Operation', value: e.companyProfile?.yearsInOperation },
                { label: 'Domain Age', value: whois?.domainAge != null ? `${whois.domainAge} years` : 'Unknown' },
              ].map(item => (
                <div key={item.label}>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{item.label}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{fmt(item.value)}</p>
                </div>
              ))}
            </div>
          </Section>

          {e.companyProfile?.leadership?.length > 0 && (
            <Section title={`Leadership (${e.companyProfile.leadership.length} identified)`} subtitle="Names and titles from public leadership pages">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {e.companyProfile.leadership.map((person, i) => (
                  <div key={i} className="flex items-start gap-3 pb-2 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-semibold text-gray-600 dark:text-gray-300">
                      {person.name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{person.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{person.title}</p>
                      {person.source && (
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 italic">Source: {person.source}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 italic">
                Contact information not publicly disclosed. Obtain directly during client engagement.
              </p>
            </Section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Technology Profile">
              <div className="space-y-2.5">
                {[
                  { label: 'Email Platform', value: e.technologyProfile?.emailPlatform },
                  { label: 'Cloud Platform', value: e.technologyProfile?.cloudPlatform },
                  { label: 'Identity Platform', value: e.technologyProfile?.identityPlatform },
                  { label: 'DNS Provider', value: e.technologyProfile?.dnsProvider || dns?.dnsProvider },
                  { label: 'Hosting', value: e.technologyProfile?.hostingProvider },
                  { label: 'Website', value: e.technologyProfile?.websiteTechnology },
                ].map(item => (
                  <div key={item.label} className="grid grid-cols-[110px_1fr] gap-2 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{item.label}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{fmt(item.value)}</span>
                  </div>
                ))}
                {(e.technologyProfile?.detectedServices || dns?.detectedServices)?.length > 0 && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">Detected Services</p>
                    <div className="flex flex-wrap gap-1">
                      {[...new Set([...(e.technologyProfile?.detectedServices || []), ...(dns?.detectedServices || [])])].map((s, i) => <Badge key={i}>{s}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            </Section>

            <Section title="Regulatory Profile">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  {(e.regulatoryProfile?.applicableFrameworks || []).map((fw, i) => <Badge key={i} color="blue">{fw}</Badge>)}
                </div>
                <div className="space-y-1.5">
                  {[
                    { label: 'Publicly Traded', value: e.regulatoryProfile?.publiclyTraded },
                    { label: 'Government Contractor', value: e.regulatoryProfile?.governmentContractor },
                    { label: 'Processes Payments', value: e.regulatoryProfile?.processesPayments },
                    { label: 'Handles Health Data', value: e.regulatoryProfile?.handlesHealthData },
                    { label: 'International Operations', value: e.regulatoryProfile?.operatesInternationally },
                  ].filter(item => item.value).map(item => (
                    <div key={item.label} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0 bg-green-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                    </div>
                  ))}
                </div>
                {e.regulatoryProfile?.dataTypes?.length > 0 && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">Sensitive Data Types</p>
                    <div className="flex flex-wrap gap-1">
                      {e.regulatoryProfile.dataTypes.map((dt, i) => <Badge key={i} color="purple">{dt}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            </Section>
          </div>

          {whois && !whois.error && (
            <Section title="Domain Registration" subtitle="WHOIS / RDAP data">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-xs text-gray-400">Registrar</p><p className="text-sm font-medium text-gray-900 dark:text-white">{fmt(whois.registrar)}</p></div>
                <div><p className="text-xs text-gray-400">Registered</p><p className="text-sm font-medium text-gray-900 dark:text-white">{whois.registrationDate ? new Date(whois.registrationDate).toLocaleDateString() : 'Unknown'}</p></div>
                <div><p className="text-xs text-gray-400">Expires</p><p className="text-sm font-medium text-gray-900 dark:text-white">{whois.expirationDate ? new Date(whois.expirationDate).toLocaleDateString() : 'Unknown'}</p></div>
                <div><p className="text-xs text-gray-400">Privacy Protected</p><p className="text-sm font-medium text-gray-900 dark:text-white">{whois.privacyProtected ? 'Yes' : 'No'}</p></div>
              </div>
            </Section>
          )}
        </div>
      )}

      {/* Security Posture */}
      {activeTab === 'security' && (
        <div className="space-y-5">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start gap-5">
              {e.securityPosture?.overallGrade && <Grade grade={e.securityPosture.overallGrade} size="lg" />}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">External Security Grade</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{e.securityPosture?.overallGradeRationale}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: 'Email Security', data: e.securityPosture?.emailSecurity, extra: (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">SPF</span><Badge color={dns?.hasSPF ? 'green' : 'red'}>{dns?.hasSPF ? 'Present' : 'Missing'}</Badge></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">DMARC</span><Badge color={dns?.hasDMARC ? 'green' : 'red'}>{dns?.hasDMARC ? `${dns.dmarcPolicy || 'present'}` : 'Missing'}</Badge></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">DKIM</span><Badge color={dns?.hasDKIM ? 'green' : 'amber'}>{dns?.hasDKIM ? 'Present' : 'Not detected'}</Badge></div>
                </div>
              )},
              { title: 'TLS / Certificate', data: e.securityPosture?.certificateSecurity, gradeOverride: sslLabs?.status === 'ready' ? sslLabs.grade : null, extra: (
                <div className="space-y-2">
                  {ssl && !ssl.error && (
                    <>
                      <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Issuer</span><span className="font-medium text-gray-900 dark:text-white text-xs truncate max-w-[140px]">{ssl.issuer}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Expires</span>
                        <Badge color={ssl.isExpired ? 'red' : ssl.isExpiringSoon ? 'amber' : 'green'}>{ssl.isExpired ? 'Expired' : `${ssl.daysUntilExpiry}d`}</Badge>
                      </div>
                    </>
                  )}
                  {sslLabs?.status === 'ready' && (
                    <>
                      <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">HSTS</span><Badge color={sslLabs.hsts === 'present' ? 'green' : 'amber'}>{sslLabs.hsts || 'Missing'}</Badge></div>
                      <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">OCSP Staple</span><Badge color={sslLabs.ocspStapling ? 'green' : 'gray'}>{sslLabs.ocspStapling ? 'On' : 'Off'}</Badge></div>
                      {sslLabs.findings?.length > 0 && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 pt-1">
                          {sslLabs.findings.length} finding{sslLabs.findings.length > 1 ? 's' : ''}: {sslLabs.findings.slice(0, 2).map(f => f.issue).join('; ')}{sslLabs.findings.length > 2 ? '…' : ''}
                        </p>
                      )}
                      {sslLabs.reportUrl && (
                        <a href={sslLabs.reportUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline">View SSL Labs report →</a>
                      )}
                    </>
                  )}
                  {sslLabs?.status === 'pending' && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">SSL Labs scan queued — refresh to see grade</p>
                  )}
                </div>
              )},
              { title: 'HTTP Headers', data: { grade: observatory?.status === 'ready' ? observatory.grade : null, notes: observatory?.status === 'ready' ? `${observatory.passedCount}/${observatory.totalCount} tests passed` : observatory?.status === 'error' ? 'Mozilla Observatory unavailable' : 'Not yet run' }, extra: (
                <div className="space-y-2">
                  {observatory?.status === 'ready' && (
                    <>
                      <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Score</span><span className="font-medium text-gray-900 dark:text-white text-xs tabular-nums">{observatory.score ?? '—'}</span></div>
                      {observatory.failedTests?.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[11px] text-gray-400">Failed:</p>
                          <div className="flex flex-wrap gap-1">
                            {observatory.failedTests.slice(0, 4).map((t, i) => (
                              <Badge key={i} color="amber">{t.name.replace(/-/g, ' ')}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {observatory.reportUrl && (
                        <a href={observatory.reportUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline">View Observatory report →</a>
                      )}
                    </>
                  )}
                </div>
              )},
              { title: 'DNS Security', data: e.securityPosture?.dnsSecurity, extra: (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Provider</span><span className="font-medium text-gray-900 dark:text-white text-xs">{dns?.dnsProvider}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">CAA</span><Badge color={dns?.hasCAA ? 'green' : 'amber'}>{dns?.hasCAA ? 'Present' : 'Missing'}</Badge></div>
                </div>
              )}
            ].map(({ title, data, extra, gradeOverride }) => (
              <Section key={title} title={title}>
                <div className="flex items-start gap-3 mb-3">
                  <Grade grade={gradeOverride || data?.grade} size="sm" />
                  <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">{data?.notes}</p>
                </div>
                {extra}
              </Section>
            ))}
          </div>

          {/* Attack surface */}
          {e.securityPosture?.attackSurface && (
            <Section title="External Attack Surface" subtitle={`${enrichmentData?.ct_subdomains?.length || 0} subdomains discovered via certificate transparency`}>
              {e.securityPosture.attackSurface.notableSubdomains?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-400 mb-1.5">Notable Subdomains</p>
                  <div className="flex flex-wrap gap-1.5">
                    {e.securityPosture.attackSurface.notableSubdomains.map((s, i) => (
                      <Badge key={i} color={['vpn', 'mail', 'remote', 'owa', 'portal', 'api', 'admin', 'dev', 'staging', 'test'].some(k => s.toLowerCase().includes(k)) ? 'amber' : 'gray'}>{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {enrichmentData?.shodan?.ports?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-400 mb-1.5">Open Ports {enrichmentData.shodan.ip ? `(${enrichmentData.shodan.ip})` : ''}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {enrichmentData.shodan.ports.map((port, i) => (
                      <Badge key={i} color={[22, 23, 3389, 445, 139, 21].includes(port) ? 'red' : [80, 443].includes(port) ? 'green' : 'amber'}>{port}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {enrichmentData?.shodan?.vulns?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-400 mb-1.5">Known Vulnerabilities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {enrichmentData.shodan.vulns.map((cve, i) => <Badge key={i} color="red">{cve}</Badge>)}
                  </div>
                </div>
              )}
              {e.securityPosture.attackSurface.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{e.securityPosture.attackSurface.notes}</p>}
              {enrichmentData?.ct_subdomains?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">View all {enrichmentData.ct_subdomains.length} discovered subdomains</summary>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {enrichmentData.ct_subdomains.map((s, i) => <Badge key={i} color={['vpn', 'mail', 'remote', 'owa', 'portal', 'api', 'admin', 'dev', 'staging'].some(k => s.includes(k)) ? 'amber' : 'gray'}>{s}</Badge>)}
                  </div>
                </details>
              )}
            </Section>
          )}

          {/* Observations */}
          <Section title="Security Observations">
            <div className="space-y-3">
              {(e.securityPosture?.initialObservations || []).map((obs, i) => {
                const text = typeof obs === 'string' ? obs : obs.observation
                const why = typeof obs === 'string' ? null : obs.whyItMatters
                const wtd = typeof obs === 'string' ? null : obs.whatToDo
                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5" />
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{text}</p>
                      {why && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 italic">Why it matters: {why}</p>}
                      {wtd && <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">What to do: {wtd}</p>}
                    </div>
                  </div>
                )
              })}
            </div>
          </Section>

          <Section title="Quick Wins">
            <div className="space-y-3">
              {(e.securityPosture?.quickWins || []).map((qw, i) => {
                const text = typeof qw === 'string' ? qw : qw.action
                const wtd = typeof qw === 'string' ? null : qw.whatToDo
                const loe = typeof qw === 'string' ? null : qw.levelOfEffort
                const loeColor = loe?.toLowerCase().startsWith('low') ? 'green' : loe?.toLowerCase().startsWith('med') ? 'amber' : loe?.toLowerCase().startsWith('high') ? 'red' : 'gray'
                const loeColors = { green: 'bg-green-100 text-green-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700', gray: 'bg-gray-100 text-gray-600' }
                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-700 dark:text-gray-300">{text}</p>
                        {loe && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${loeColors[loeColor]}`}>Effort: {loe}</span>}
                      </div>
                      {wtd && <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">What to do: {wtd}</p>}
                    </div>
                  </div>
                )
              })}
            </div>
          </Section>
        </div>
      )}

      {/* Engagement Intel */}
      {activeTab === 'engagement' && (
        <div className="space-y-5">
          {e.engagementContext?.maturityEstimate && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Estimated Maturity Level</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{e.engagementContext.maturityEstimate}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{e.engagementContext.maturityRationale}</p>
                </div>
                {e.engagementContext?.threatProfileRecommendation && (
                  <div className="text-right max-w-[280px]">
                    <p className="text-xs text-gray-400 mb-1">Recommended Threat Profile</p>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{e.engagementContext.threatProfileRecommendation}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Key Risks">
              <div className="space-y-2">
                {(e.engagementContext?.keyRisks || []).map((risk, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5" />
                    <p className="text-sm text-gray-700 dark:text-gray-300">{risk}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Suggested Focus Areas">
              <div className="space-y-2">
                {(e.engagementContext?.suggestedFocusAreas || []).map((area, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5" />
                    <p className="text-sm text-gray-700 dark:text-gray-300">{area}</p>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {e.engagementContext?.conversationStarters?.length > 0 && (
            <Section title="First Meeting Conversation Starters">
              <div className="space-y-2">
                {e.engagementContext.conversationStarters.map((q, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#012438' }}>{i + 1}</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{q}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* Confidence footer */}
      {e.confidence && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Badge color={e.confidence.overall === 'high' ? 'green' : e.confidence.overall === 'medium' ? 'amber' : 'red'}>{e.confidence.overall} confidence</Badge>
          <span className="text-xs text-gray-500 dark:text-gray-400">{e.confidence.notes}</span>
        </div>
      )}

      {/* Regenerate modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRegenerateModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Regenerate Intelligence</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Update details before regenerating the analysis</p>
              </div>
              <button onClick={() => setShowRegenerateModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization Name</label>
                <input
                  type="text"
                  value={regenName}
                  onChange={(ev) => setRegenName(ev.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Domain</label>
                <input
                  type="text"
                  value={regenDomain}
                  onChange={(ev) => setRegenDomain(ev.target.value)}
                  placeholder="e.g., acmefinancial.com"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Additional Context <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={regenContext}
                  onChange={(ev) => setRegenContext(ev.target.value)}
                  placeholder="e.g., 200 employees, processes credit cards"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRegenerateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                disabled={!regenDomain.trim()}
                className="btn-primary text-sm disabled:opacity-50"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientIntelligenceTab
